import IUser from '../../components/entities/user';
import { AccountType } from '../../components/login/NativeLogin';

export type UnknownRecord = Record<string, unknown>;

export function isRecord(value: unknown): value is UnknownRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readStringField(obj: UnknownRecord, key: string): string | undefined {
    const v = obj[key];
    return typeof v === 'string' ? v : undefined;
}

/** Backend may use `status`, `message`, or HTTP-style success flags — normalize here. */
function isApiSuccessPayload(obj: UnknownRecord): boolean {
    const status = readStringField(obj, 'status');
    const message = readStringField(obj, 'message');
    const success = obj.success;
    if (success === true) return true;
    if (typeof status === 'string' && /^success$/i.test(status)) return true;
    if (typeof message === 'string' && /^success$/i.test(message)) return true;
    return false;
}

export type SendOtpParsed =
    | { ok: true }
    | { ok: false; errorMessage: string };

export function parseSendOtpResponse(raw: unknown): SendOtpParsed {
    if (raw === null || raw === undefined) {
        return { ok: false, errorMessage: 'empty_response' };
    }
    if (!isRecord(raw)) {
        return { ok: false, errorMessage: 'invalid_response' };
    }
    if (isApiSuccessPayload(raw)) {
        return { ok: true };
    }
    const err =
        readStringField(raw, 'error') ||
        readStringField(raw, 'message') ||
        readStringField(raw, 'title') ||
        'send_otp_failed';
    return { ok: false, errorMessage: err };
}

function hasAccessToken(data: UnknownRecord): data is UnknownRecord & { accessToken: string } {
    return typeof data.accessToken === 'string';
}

export type VerifyOtpParsed =
    | { ok: true; user: IUser }
    | { ok: false; errorMessage: string };

export function parseVerifyOtpResponse(raw: unknown): VerifyOtpParsed {
    if (raw === null || raw === undefined) {
        return { ok: false, errorMessage: 'empty_response' };
    }
    if (!isRecord(raw)) {
        return { ok: false, errorMessage: 'invalid_response' };
    }
    if (!isApiSuccessPayload(raw)) {
        const err =
            readStringField(raw, 'error') ||
            readStringField(raw, 'message') ||
            readStringField(raw, 'title') ||
            'verify_otp_failed';
        return { ok: false, errorMessage: err };
    }
    const nested = raw.data;
    const userRecord: UnknownRecord = isRecord(nested) ? nested : raw;
    if (!hasAccessToken(userRecord)) {
        return { ok: false, errorMessage: 'invalid_user_payload' };
    }
    const accountType = typeof userRecord.accountType === 'number' ? userRecord.accountType : AccountType.USER;
    const user = { ...userRecord, accessToken: userRecord.accessToken, accountType } as IUser;
    return { ok: true, user };
}
